import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FieldGroup } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import type { WBS } from '../data/databaseSchema';
import { wbsService } from '../services/wbsService';

interface WbsFormData {
  task_code: string;
  project_type: string;
  name: string;
  billable: boolean;
}

interface WbsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (wbs: WBS) => void;
  onEntrySaved?: () => void;
  wbs?: WBS | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const WbsDrawer: React.FC<WbsDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  wbs,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<WbsFormData>({
    task_code: '',
    project_type: 'internal',
    name: '',
    billable: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && wbs) {
        setFormData({
          task_code: wbs.task_code || '',
          project_type: wbs.project_type || 'internal',
          name: wbs.name || '',
          billable: wbs.billable ?? false,
        });
      } else {
        setFormData({
          task_code: '',
          project_type: 'internal',
          name: '',
          billable: false,
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, wbs]);

  const handleInputChange = (field: keyof WbsFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.task_code || formData.task_code.trim() === '') {
      newErrors.task_code = 'Task code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData = {
        task_code: formData.task_code.trim(),
        project_type: formData.project_type,
        name: formData.name.trim() || undefined,
        billable: formData.billable,
      };

      const response =
        isEdit && wbs
          ? await wbsService.updateWbs(wbs.id, apiData)
          : await wbsService.createWbs(apiData);

      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypeLabel = useMemo(() => {
    const type = formData.project_type.trim().toLowerCase();
    if (type === 'internal') return 'Internal';
    if (type === 'external') return 'External';
    return formData.project_type || 'N/A';
  }, [formData.project_type]);

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View WBS Item' : isEdit ? 'Edit WBS Item' : 'Add WBS Item'}
      description={
        readOnly
          ? 'WBS item details.'
          : isEdit
            ? 'Update the WBS item details below.'
            : 'Fill in the details for the new WBS item.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update WBS Item' : 'Add WBS Item'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="Task Code"
          required
          value={formData.task_code}
          onChange={(v) => handleInputChange('task_code', v)}
          error={errors.task_code}
          placeholder="Enter task code"
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        {readOnly ? (
          <TextFormField
            label="Project Type"
            required
            value={projectTypeLabel}
            onChange={() => {}}
            disabled
            copyable
          />
        ) : (
          <SelectFormField
            label="Project Type"
            required
            value={formData.project_type}
            onChange={(v) => handleInputChange('project_type', v)}
            options={[
              { value: 'internal', label: 'Internal' },
              { value: 'external', label: 'External' },
            ]}
          />
        )}
        <TextFormField
          label="Name"
          value={formData.name}
          onChange={(v) => handleInputChange('name', v)}
          placeholder="Enter WBS item name"
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="billable"
            checked={formData.billable}
            onCheckedChange={(checked) => handleInputChange('billable', checked === true)}
            disabled={fieldsDisabled}
          />
          <Label
            htmlFor="billable"
            className={readOnly ? 'font-normal' : 'cursor-pointer font-normal'}
          >
            Billable
          </Label>
        </div>
      </FieldGroup>
    </FormDetailPane>
  );
};
